/**
 * Validation Schemas for TokenSurvey Wizard
 *
 * Uses Zod for runtime validation and TypeScript type inference
 */

import { z } from 'zod';
import type { Language } from '@/contexts/SettingsContext';
import { VALIDATION_SCHEMA_MESSAGES } from '@/constants/translations';

function createActivationCodeSchema(t: typeof VALIDATION_SCHEMA_MESSAGES['ko']) {
  return z.object({
    activationCode: z
      .string()
      .min(4, t.activationCodeRequired)
      .max(6, t.activationCodeLength)
      .regex(/^[A-Z0-9]+$/, t.activationCodeFormat),
  });
}

function createBirthDateSchema(t: typeof VALIDATION_SCHEMA_MESSAGES['ko']) {
  return z.object({
    birthDate: z
      .string()
      .min(1, t.birthDateRequired)
      .regex(/^\d+$/, t.birthDateDigitsOnly),
  });
}

function createSessionSelectionSchema(t: typeof VALIDATION_SCHEMA_MESSAGES['ko']) {
  return z.object({
    selectedSessionId: z.string().min(1, t.sessionRequired),
  });
}

/** Default schemas (Korean) for type inference */
export const activationCodeSchema = createActivationCodeSchema(VALIDATION_SCHEMA_MESSAGES.ko);
export const birthDateSchema = createBirthDateSchema(VALIDATION_SCHEMA_MESSAGES.ko);
export const sessionSelectionSchema = createSessionSelectionSchema(VALIDATION_SCHEMA_MESSAGES.ko);

export type ActivationCodeFormData = z.infer<typeof activationCodeSchema>;
export type BirthDateFormData = z.infer<typeof birthDateSchema>;
export type SessionSelectionFormData = z.infer<typeof sessionSelectionSchema>;

/**
 * Get schema for current step with i18n support
 */
export function getSchemaForStep(step: string, lang: Language = 'ko') {
  const t = VALIDATION_SCHEMA_MESSAGES[lang];
  switch (step) {
    case 'activation-code':
      return createActivationCodeSchema(t);
    case 'birth-date':
      return createBirthDateSchema(t);
    case 'session-list':
      return createSessionSelectionSchema(t);
    default:
      return z.object({});
  }
}
